import { Delete, Edit, Add as AddIcon, Percent } from "@mui/icons-material";
import { Box, Button } from "@mui/material";
import { DataGridPremium, GridCellParams, GridColDef } from "@mui/x-data-grid-premium";
import { Contact, useContractedCompaniesByIdsLazyQuery } from "@repo/server-api";
import { ContractedInformation } from "@repo/server-api/types";
import { useTranslation } from "next-i18next";
import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";

import { useDeleteContractedInformation } from "../../hooks/ContactMutationHooks";
import ConformationDialog from "../dialogs/Conformation";
import DialogContractedCompany from "../dialogs/DialogContractedCompany";
import NumberTextField from "../fields/NumberTextField";

const ContractedCompanies = forwardRef(
	(
		props: Readonly<{
			contact: Contact | null;
			setContact: (value: Contact | null) => void;
			errors?: Record<string, boolean | object | string>;
			setErrors?: (value: Record<string, boolean | object | string>) => void;
		}>,
		ref: React.Ref<{
			validate: () => Promise<Record<string, boolean | object | string>>;
		}>,
	) => {
		const { t } = useTranslation();

		const [companies, setCompanies] = useState<ContractedInformation[]>([]);
		const [company, setCompany] = useState<ContractedInformation | null>(null);
		const [openDialog, setOpenDialog] = useState<boolean>(false);

		const [isLoaded, setIsLoaded] = useState<boolean>(false);

		const [deleteContractedInformationExecute] = useDeleteContractedInformation();

		const deleteContractedInformation = (key: number, id: string) => {
			const list = companies.filter((c) => c.key !== key);
			props.setContact(
				props.contact
					? {
							...props.contact,
							additionalInformation: [
								...(props.contact.additionalInformation || []).filter(
									(info) => (info as ContractedInformation).__typename !== "ContractedInformation",
								),
								...list,
							],
						}
					: null,
			);
			setCompanies(list);

			// TODO: Rempove this when you fix custom hooks
			if (props.contact?.id && id !== "new" && id) {
				deleteContractedInformationExecute(props.contact.id, id);
			}
		};

		const validateFields = useCallback(async () => {
			const localErrors: Record<string, boolean | object | string> = {};

			props.setErrors?.(localErrors);

			return localErrors;
		}, [props]);

		useImperativeHandle(ref, () => ({
			validate: validateFields,
		}));

		useEffect(() => {
			if (props.contact && !isLoaded) {
				const contractedCompaniesInfo: ContractedInformation[] = [];
				props.contact.additionalInformation.forEach((i) => {
					if ((i as ContractedInformation).__typename === "ContractedInformation") {
						contractedCompaniesInfo.push({
							...(i as ContractedInformation),
							key: Math.random() * 1000,
						});
					}
				});
				setCompanies(contractedCompaniesInfo);
				setIsLoaded(true);
				getCompanies();
			}
		}, [props.contact]);

		useEffect(() => {
			if (props.contact && props.setContact && isLoaded) {
				props.setContact({
					...props.contact,
					additionalInformation: [
						...(props.contact.additionalInformation?.filter(
							(i) => (i as ContractedInformation).__typename !== "ContractedInformation",
						) || []),
						...companies,
					],
				});
			}
		}, [companies]);

		const [getCompanies] = useContractedCompaniesByIdsLazyQuery({
			fetchPolicy: "no-cache",
			variables: { ids: companies.map((c) => c.contractedId) },
			onCompleted: (data) => {
				if (data && data.contactsByIds) {
					const newCompanies = data.contactsByIds.map((c) => {
						const cc = companies.find((cc) => cc.contractedId === c.id);
						return {
							...cc,
							displayName: c.displayName,
							key: Math.random() * 1000,
						};
					});
					setCompanies(newCompanies as ContractedInformation[]);
				}
			},
		});

		const columns: GridColDef<ContractedInformation>[] = [
			{
				field: "name",
				headerName: t("name"),
				width: 250,
				renderCell: (params: GridCellParams) => {
					return params.row.displayName;
				},
			},
			{
				field: "marge",
				headerName: t("margin"),
				width: 200,
				renderCell: (params: GridCellParams) => {
					return (
						<NumberTextField
							isLoaded={true}
							value={params.row.marge}
							icon={<Percent />}
							flat={true}
							readOnly={true}
							notGrey={true}
							integer={true}
						/>
					);
				},
			},
			{
				field: "actions",
				headerName: t("actions"),
				sortable: false,
				width: 250,
				renderCell: (params: GridCellParams) => {
					const [openDeleteConformation, setOpenDeleteConformation] = useState<boolean>(false);

					const handleYes = (key: number, id: string) => {
						deleteContractedInformation(key, id);
						setOpenDeleteConformation(false);
					};

					const openDelete = () => {
						setOpenDeleteConformation(true);
					};

					const openEdit = (c: ContractedInformation) => {
						setCompany(c);
						setOpenDialog(true);
					};

					return (
						<div>
							<Button data-testid="edit-button" onClick={() => openEdit(params.row)}>
								<Edit />
							</Button>
							<Button data-testid="delete-button" onClick={() => openDelete()}>
								<Delete />
							</Button>
							<ConformationDialog
								text={t("contracted-info.delete-conformation")}
								setOpenDeleteConformation={setOpenDeleteConformation}
								openPopup={openDeleteConformation}
								handleYes={() => handleYes(params.row.key, params.row.id.toString())}
							/>
						</div>
					);
				},
			},
		];

		const clickOnAdd = () => {
			setCompany({
				key: companies.length,
				id: "",
				contractedId: "",
				marge: 0,
			});
			setOpenDialog(true);
		};

		return (
			<div>
				<Box sx={{ height: "100%", width: "100%" }}>
					<Box className="clear">
						<Button variant="contained" className="greenButton" onClick={() => clickOnAdd()}>
							{t("add")} <AddIcon />
						</Button>
					</Box>
					<DialogContractedCompany
						refreshData={(c: ContractedInformation) => {
							const findCompany = companies.find((cc) => cc.id === c.id);
							if (findCompany) {
								const index = companies.indexOf(findCompany);
								const newCompanies = [...companies];
								newCompanies[index] = c;
								setCompanies(newCompanies);
							} else {
								setCompanies([...companies, c]);
							}
						}}
						setCompany={setCompany}
						company={company}
						open={openDialog}
						setOpen={setOpenDialog}
						contactId={props.contact?.id ?? ""}
					/>
					{companies.length ? (
						<DataGridPremium
							getRowId={(row: ContractedInformation) => (row.key ? row.key : "")}
							rows={companies}
							columns={columns}
							disableRowSelectionOnClick
							className="table"
						/>
					) : (
						""
					)}
				</Box>
			</div>
		);
	},
);

ContractedCompanies.displayName = "ContractedCompanies";
export default ContractedCompanies;
